import React, {PureComponent} from "react"
import {cloneDeep} from "lodash"
import {MutationFn, MutationResult} from "react-apollo"

import {User} from "@irati/types"
import Card from "@irati/ui/src/Layout/Card"
import TenantWalletCreateRefund from "@irati/wallet/apollo/mutation/TenantWalletCreateRefund"
import TenantWalletBalanceQuery, {TenantWalletBalanceQueryResult} from "@irati/wallet/apollo/query/TenantWallet"
import RewardWalletBalanceQuery, {RewardWalletBalanceData} from "@irati/wallet/apollo/query/RewardWallet"
import {FormattedMessage} from "@irati/utils/lang"
import History from "@irati/utils/history"
import tracking from "@irati-tracking/wallet/tenantWallet"
import authentication from "@irati/auth"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import ErrorText from "@irati/ui/src/ErrorText"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import TenantWalletTransferType from "@irati/wallet/enums/TenantWalletTransferType"

import {CreditTransferConfirmationPopup} from "../WalletPopups"
import Amount from "../Amount"
import SelectManager from "./SelectManager"
import Totals from "./Totals"

const DEFAULT_AMOUNT = "10.00"

interface Props {
  id: string
}

interface State {
  amount: string,
  recipients: User[],
  showConfirmation: boolean
}

class TenantWalletRefund extends PureComponent<Props, State> {
  state: State = {
    amount: DEFAULT_AMOUNT,
    recipients: [],
    showConfirmation: false
  }

  onCompleted = () => {
    tracking.completeTenantWalletTransferCredit()
    this.setState({showConfirmation: true})
  }

  addRecipient = (user: User) => {
    tracking.selectRefundManager()
    const recipients = cloneDeep(this.state.recipients)
    recipients.push(user)
    this.setState({recipients})
  }

  removeRecipient = (user: User) => {
    const recipients = cloneDeep(this.state.recipients).filter((u) => u.uuid !== user.uuid)
    this.setState({recipients})
  }

  updateAmount = (amount: string) => {
    tracking.enterRefundValue()
    this.setState({amount})
  }

  handleClose = () => {
    History.push({
      pathname: "/wallet/tenant"
    })
  }

  handleRepeatAgain = () => {
    this.setState({showConfirmation: false, recipients: [], amount: DEFAULT_AMOUNT})
  }

  returnRewardWalletBalanceVariables = (user: User) => ({
    currency: authentication.defaultUserCurrency(),
    tenantId: authentication.userTenant().uuid,
    userId: user.uuid
  })

  handleClick = (canRefund: boolean, mutate: MutationFn) => {
    if (canRefund) {
      tracking.clickRefundCTA()
      mutate()
    }
  }

  render() {
    const {amount, recipients, showConfirmation} = this.state
    const {id} = this.props
    return (
      <Card>
        <TenantWalletBalanceQuery>
          {({data}: TenantWalletBalanceQueryResult) => {
            const {currency, availableBalance} = data.tenantWallet
            const variables = {
              amount,
              currency,
              senderId: recipients[0] ? recipients[0].uuid : null,
              tenantId: authentication.userTenant().uuid,
              reason: ""
            }
            const refundAmount = formatCurrency({currencyCode: currency, isCents: false, value: amount})

            const updatedBalance = {
              amount: Number(amount) + availableBalance,
              currency
            }

            return (
              <TenantWalletCreateRefund onCompleted={this.onCompleted} variables={variables}>
                {(mutate: MutationFn, apolloState: MutationResult) => {
                  return (
                    <>
                      <Amount
                        amount={amount}
                        id={id}
                        name="TenantWalletRefundAmount"
                        label="wallet.tenant.refund.amount.label"
                        updateAmount={this.updateAmount} />

                      <SelectManager addRecipient={this.addRecipient} recipients={recipients} removeRecipient={this.removeRecipient} />

                      {recipients[0] && (
                        <RewardWalletBalanceQuery useLoader={false} variables={this.returnRewardWalletBalanceVariables(recipients[0])}>
                          {({data: {rewardWallet}, loading}: RewardWalletBalanceData) => {
                            if (loading) return null
                            const cannotRefund = rewardWallet && rewardWallet.availableBalance < Number(amount)

                            const updatedRewardWalletBalance = {
                              amount: rewardWallet.availableBalance - Number(amount),
                              currency
                            }

                            return (
                              <>
                                <Totals
                                  amount={Number(amount)}
                                  currency={currency}
                                  recipients={recipients}
                                  updatedRewardWalletBalance={updatedRewardWalletBalance}
                                  updatedBalance={updatedBalance}
                                  wallet={rewardWallet} />
                                {cannotRefund && (
                                  <ErrorText>
                                    <FormattedMessage id="wallet.tenant.refund.amountError" />
                                  </ErrorText>
                                )}
                                <ValidationButton
                                  apolloState={apolloState}
                                  full
                                  large
                                  onClick={() => this.handleClick(!cannotRefund, mutate)}
                                  primary>
                                  <FormattedMessage id="wallet.tenant.refund.button" values={{refundAmount}} />
                                </ValidationButton>
                              </>
                            )
                          }}
                        </RewardWalletBalanceQuery>
                      )}
                      {showConfirmation && (
                        <CreditTransferConfirmationPopup
                          show={showConfirmation}
                          handleClose={this.handleClose}
                          handleRepeatAgain={this.handleRepeatAgain}
                          recipients={recipients}
                          transferType={TenantWalletTransferType.REFUND}
                          amount={amount}
                          totalAmount={amount}
                          remainingAmount={updatedBalance.amount}
                          currency={currency} />
                      )}
                    </>
                  )
                }}
              </TenantWalletCreateRefund>
            )
          }}
        </TenantWalletBalanceQuery>
      </Card>
    )
  }
}

export default TenantWalletRefund
